import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef
} from 'react';
import {
  TokensClient,
  RefreshTokenRequest,
  TokenResponse,
  PersonalClient
} from '../rest/apiv2.hsdesk';
import axios from 'axios';
import { config } from '@/config';
import { User } from '@/types';

interface UserContextType {
  accessToken: string | null;
  user: User | null;
  permissions: string[] | null;
  isLoading: boolean;
  setAccessToken: React.Dispatch<React.SetStateAction<string | null>>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  setPermissions: React.Dispatch<React.SetStateAction<string[] | null>>;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[] | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const hasRefreshed = useRef(false); // 👈 flaga zabezpieczająca przed podwójnym uruchomieniem

  useEffect(() => {
    if (hasRefreshed.current) return; // 👈 zapobiegaj ponownemu wywołaniu w StrictMode
    hasRefreshed.current = true;

    const tryRefresh = async () => {
      try {
        const storedRefreshToken = sessionStorage.getItem('refreshToken');
        const storedAccessToken = sessionStorage.getItem('accessToken');

        if (!storedRefreshToken) {
          setIsLoading(false);
          return;
        }

        const client = new TokensClient(config.apiUrl, axios);
        const request = new RefreshTokenRequest({
          token: storedAccessToken ?? undefined,
          refreshToken: storedRefreshToken
        });

        const response: TokenResponse = await client.refresh(
          config.tenantId,
          request
        );

        if (response.token && response.refreshToken) {
          setAccessToken(response.token);
          sessionStorage.setItem('accessToken', response.token);
          sessionStorage.setItem('refreshToken', response.refreshToken);

          const authorizedAxios = axios.create({
            baseURL: config.apiUrl,
            headers: {
              Authorization: `Bearer ${response.token}`
            }
          });
          const permissionResponse: string[] | null = await new PersonalClient(
            config.apiUrl,
            authorizedAxios
          ).getPermissions();
          console.log('Permissions:', permissionResponse);
          if (permissionResponse) {
            // Convert the array to a JSON string before storing
            sessionStorage.setItem(
              'permissions',
              JSON.stringify(permissionResponse)
            );
          } else {
            // Handle the case where permissions might be null or undefined
            // You might want to remove the item or store an empty array string
            sessionStorage.removeItem('permissions'); // Or sessionStorage.setItem("permissions", JSON.stringify([]));
          }

          setPermissions(permissionResponse);

          // Jeśli potrzebujesz usera – pobierz go osobno tutaj
          const userData: User = {
            id: 1,
            firstName: 'Jan',
            lastName: 'Kowalski',
            imgUrl: 'https://krzepicki.pl/assets/img/profile-img.jpg',
            userName: 'JanK',
            phone: {
              number: '132',
              verified: false
            },
            sex: 'male',
            birthday: '',
            lang: 'en',
            country: 'string',
            city: 'string',
            address1: 'string',
            zipcode: 60278,
            email: {
              name: ' data.email',
              verified: false
            },
            name: 'data.email'
          };
          setUser(userData);
        } else {
          clearTokens();
        }
      } catch (err) {
        console.warn('Sesja wygasła lub brak aktywnej sesji');
        clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    tryRefresh();
  }, []);

  const clearTokens = () => {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    setAccessToken(null);
    setPermissions(null);
    setUser(null);
  };

  const logout = () => {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    setAccessToken(null);
    setPermissions(null);
    setUser(null);
    console.log('logout');
    // Jeśli używasz React Routera:
    // navigate("/login");
  };

  return (
    <UserContext.Provider
      value={{
        accessToken,
        setAccessToken,
        permissions,
        setPermissions,
        user,
        setUser,
        isLoading,
        logout
      }}
    >
      {!isLoading && children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context)
    throw new Error('useUser musi być używany wewnątrz <UserProvider>');
  return context;
};
